import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  ShoppingCart,
  Package,
  DollarSign,
  Users,
  BarChart3,
  PieChart,
  Activity,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { getCartAnalyticsThunk } from "../../../store/thunks/adminCartThunks";
import LoadingSpinner from "../../../components/common/Loader";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const CartAnalytics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartAnalytics, loadingAnalytics, error } = useSelector(
    (state) => state.cart
  );

  useEffect(() => {
    dispatch(getCartAnalyticsThunk());
  }, [dispatch]);

  if (loadingAnalytics) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading analytics: {error}
        </div>
      </div>
    );
  }

  const stats = cartAnalytics?.overall || {};
  const exchangeStats = cartAnalytics?.exchangeItems || [];
  const abandonedByAge = cartAnalytics?.abandonedCartsByAge || [];

  // Chart data for cart status distribution
  const statusChartData = {
    labels: ["Active", "Abandoned", "Converted"],
    datasets: [
      {
        data: [
          stats.activeCarts || 0,
          stats.abandonedCarts || 0,
          stats.convertedCarts || 0,
        ],
        backgroundColor: ["#10B981", "#F59E0B", "#3B82F6"],
        borderWidth: 0,
      },
    ],
  };

  // Chart data for abandoned cart age
  const abandonedChartData = {
    labels: abandonedByAge.map((item) =>
      item._id === "90+" ? "90+ days" : `${item._id} days`
    ),
    datasets: [
      {
        label: "Abandoned Carts",
        data: abandonedByAge.map((item) => item.count),
        backgroundColor: "#F59E0B",
        borderColor: "#D97706",
        borderWidth: 1,
      },
    ],
  };

  // Chart data for exchange items
  const exchangeChartData = {
    labels: exchangeStats.map(
      (item) => item._id?.replace(/_/g, " ") || "Unknown"
    ),
    datasets: [
      {
        data: exchangeStats.map((item) => item.count),
        backgroundColor: ["#F59E0B", "#10B981", "#EF4444"],
      },
    ],
  };

  const metricCards = [
    {
      title: "Total Carts",
      value: stats.totalCarts || 0,
      icon: ShoppingCart,
      trend: null,
      color: "blue",
    },
    {
      title: "Conversion Rate",
      value: stats.conversionRate || "0%",
      icon: TrendingUp,
      trend: "up",
      trendValue: "+2.5%",
      color: "green",
    },
    {
      title: "Avg Items/Cart",
      value: stats.avgItemsPerCart?.toFixed(1) || 0,
      icon: Package,
      trend: null,
      color: "purple",
    },
    {
      title: "Exchange Carts",
      value: stats.cartsWithExchange || 0,
      icon: Activity,
      trend: "up",
      trendValue: "+12%",
      color: "orange",
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Cart Analytics</h1>
        <p className="text-gray-600 mt-2">
          Comprehensive insights into shopping cart performance
        </p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {metricCards.map((metric, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-full bg-${metric.color}-100`}>
                <metric.icon className={`w-6 h-6 text-${metric.color}-600`} />
              </div>
              {metric.trend && (
                <div
                  className={`flex items-center gap-1 text-sm ${
                    metric.trend === "up" ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {metric.trend === "up" ? (
                    <ArrowUp className="w-4 h-4" />
                  ) : (
                    <ArrowDown className="w-4 h-4" />
                  )}
                  {metric.trendValue}
                </div>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
            <p className="text-sm text-gray-600 mt-1">{metric.title}</p>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Cart Status Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">
            Cart Status Distribution
          </h2>
          <div className="h-64">
            <Doughnut
              data={statusChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom",
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Abandoned Cart Age */}
        {abandonedByAge.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">
              Abandoned Cart Age Distribution
            </h2>
            <div className="h-64">
              <Bar
                data={abandonedChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
              />
            </div>
          </div>
        )}

        {/* Exchange Items Status */}
        {exchangeStats.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">
              Exchange Items Status
            </h2>
            <div className="h-64">
              <Doughnut
                data={exchangeChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                    },
                  },
                }}
              />
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate("/cart/abandoned")}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-left flex items-center justify-between"
            >
              <span>View Abandoned Carts</span>
              <span className="text-sm font-medium text-orange-600">
                {stats.abandonedCarts} carts
              </span>
            </button>
            <button
              onClick={() => navigate("/cart/exchange-items")}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-left flex items-center justify-between"
            >
              <span>Pending Verifications</span>
              <span className="text-sm font-medium text-yellow-600">
                {exchangeStats.find((s) => s._id === "PENDING_VERIFICATION")
                  ?.count || 0}{" "}
                items
              </span>
            </button>
            <button
              onClick={() => navigate("/cart/list")}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-left flex items-center justify-between"
            >
              <span>Active Carts</span>
              <span className="text-sm font-medium text-green-600">
                {stats.activeCarts} carts
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Performance Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">
              {stats.conversionRate || "0%"}
            </p>
            <p className="text-sm text-gray-600 mt-1">Conversion Rate</p>
            <p className="text-xs text-gray-500 mt-2">Industry average: 2-3%</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">
              {((stats.abandonedCarts / stats.totalCarts) * 100).toFixed(1)}%
            </p>
            <p className="text-sm text-gray-600 mt-1">Abandonment Rate</p>
            <p className="text-xs text-gray-500 mt-2">Industry average: 70%</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">
              {stats.avgItemsPerCart?.toFixed(1) || 0}
            </p>
            <p className="text-sm text-gray-600 mt-1">Avg Items per Cart</p>
            <p className="text-xs text-gray-500 mt-2">Target: 3+ items</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartAnalytics;
